/**
 ** module: SLTEditDirectoryEntry.js
 ** usage: rendering logic for editing lot in lothood directory
 ** created: 12/1/2025
 ** owner: Rob Blanch
 **/
import React from "react";
import "./SLTEditDirectoryEntry.css";

/**
 ** SLTEditDirectoryEntry
 ** usage: class used to provide UI for editing a directory entry
 **/
class SLTEditDirectoryEntry extends React.Component {
    /**
     * constructor
     * usage: rendering logic for editing a directory entry
     * props:
     *      lot - (mandatory) lot object to edit
     *      onSave(updatedEntry) - (mandatory) callback function when saving the edited lot
     *      onCancel - (mandatory) callback function when canceling the edit
     *      setNavItems(navItems) - (optional) callback to set nav items in parent component
     */
    constructor(props) {
        super(props);

        var isMissingProps = false;
        var msg = null;
        var error = null;

        if (!props.lot || typeof props.lot !== 'object') {
            isMissingProps = true;
            msg = "SLTEditDirectoryEntry: 'lot' prop is required and must be an object";
            error = new Error(msg);
        }

        if (!props.onSave || typeof props.onSave !== 'function') {
            isMissingProps = true;
            msg = "SLTEditDirectoryEntry: 'onSave' prop is required and must be a function";
            error = new Error(msg);
        }

        if (!props.onCancel || typeof props.onCancel !== 'function') {
            isMissingProps = true;
            msg = "SLTEditDirectoryEntry: 'onCancel' prop is required and must be a function";
            error = new Error(msg);
        }

        if (!props.setNavItems || typeof props.setNavItems !== "function") {
            isMissingProps = true;
            msg = "SLTEditDirectoryEntry: 'setNavItems' prop is required and must be a function";
            error = new Error(msg);
        }

        this.state = {
            isMissingProps: isMissingProps,
            isMounted: false,
            error: error,
            message: msg,
            lot: props.lot,
            saving: false,
        };
    }

    /**
     ** setSaving
     **      set saving state
     **/
    setSaving(saving) {
        this.setState({saving: saving});
    }

    /**
     ** handleLotChange
     **      handle changes to lot fields
     **/
    handleLotChange(field, value) {
        this.setState((prev) => ({ ...prev, lot: { ...prev.lot, [field]: value } }));
    }

    /**
     ** handleOwnerChange
     **     handle changes to owner fields
     **/
    handleOwnerChange(ownerIndex, field, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            owners[ownerIndex] = { ...owners[ownerIndex], [field]: value };
            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     * createEmptyOwner
     *      utility routine to create an empty owner object
     **/
    createEmptyOwner() {
        return {
            id: null,
            owner_name: '',
            phone_numbers: [{phone_number: '', phone_number_type: 'Primary', show: true}],
            email_addresses: [{email_address: '', email_address_type: 'Primary', show: true}],
            mailing_addresses: [{address_type: 'Primary', address_line1: '', address_line2: '', city: '', state_province_region: '', postal_code: '', country: '', show: true}],
        };
    }

    /**
     ** addOwner
     **     add a new owner to lot
     **/
    addOwner() {
        var createEmptyOwner = this.createEmptyOwner.bind(this);

        this.setState((prev) => ({
            ...prev,
            lot: { ...prev.lot, owners: [...prev.lot.owners, createEmptyOwner()] },
        }));
    }

    /**
     ** removeOwner
     **     remove an owner from lot
     **/
    removeOwner(ownerIndex) {
        var createEmptyOwner = this.createEmptyOwner.bind(this);

        this.setState((prev) => {
            const owners = prev.lot.owners.filter((_, idx) => idx !== ownerIndex);
            // Always keep at least one owner row
            return { ...prev, lot: { ...prev.lot, owners: owners.length ? owners : [createEmptyOwner()] } };
        });
    }

    /**
     ** moveOwnerUp
     **     move an owner up in the list (decrease index)
     **/
    moveOwnerUp(ownerIndex) {
        if (ownerIndex === 0) return; // Can't move first owner up

        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            // Swap with previous owner
            [owners[ownerIndex - 1], owners[ownerIndex]] = [owners[ownerIndex], owners[ownerIndex - 1]];
            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** moveOwnerDown
     **     move an owner down in the list (increase index)
     **/
    moveOwnerDown(ownerIndex) {
        if (ownerIndex >= this.state.lot.owners.length - 1) return; // Can't move last owner down

        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            // Swap with next owner
            [owners[ownerIndex], owners[ownerIndex + 1]] = [owners[ownerIndex + 1], owners[ownerIndex]];
            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerPhoneChange
     **      handle changes to owner phone numbers
     **/
    handleOwnerPhoneChange(ownerIndex, phoneIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const phones = [...owners[ownerIndex].phone_numbers];

            phones[phoneIndex].phone_number = value;
            owners[ownerIndex] = { ...owners[ownerIndex], phone_numbers: phones };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     * handleOwnerPhoneVisibilityChange
     *    handle show/hide change for owner phone number
     */
    handleOwnerPhoneVisibilityChange(ownerIndex, phoneIndex, show) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const phones = [...owners[ownerIndex].phone_numbers];

            phones[phoneIndex].show_public_directory = show;
            owners[ownerIndex] = { ...owners[ownerIndex], phone_numbers: phones };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** addOwnerPhone
     **      add a phone number to an owner
     **/
    addOwnerPhone(ownerIndex) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const phones = [...owners[ownerIndex].phone_numbers, {"phone_number": '', "phone_number_type": 'Primary', show_public_directory: true}];

            owners[ownerIndex] = { ...owners[ownerIndex], phone_numbers: phones };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** removeOwnerPhone
     **      remove a phone number from an owner
     **/
    removeOwnerPhone(ownerIndex, phoneIndex) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            let phones = owners[ownerIndex].phone_numbers.filter((_, idx) => idx !== phoneIndex);

            owners[ownerIndex] = { ...owners[ownerIndex], phone_numbers: phones };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    };

    /**
     ** handleOwnerEmailChange
     **      handle changes to owner email addresses 
     **/
    handleOwnerEmailChange(ownerIndex, emailIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const emails = [...owners[ownerIndex].email_addresses];

            emails[emailIndex].email_address = value;
            owners[ownerIndex] = { ...owners[ownerIndex], email_addresses: emails };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    };

    /**
     ** addOwnerEmail
     **      add an email address to an owner
     **/
    addOwnerEmail(ownerIndex) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const emails = [...owners[ownerIndex].email_addresses, {"email_address": '', "email_address_type": 'Primary', show: true}];
            owners[ownerIndex] = { ...owners[ownerIndex], email_addresses: emails };
            return { ...prev, lot: { ...prev.lot, owners } };
        });
    };

    /**
     ** removeOwnerEmail
     **      remove an email address from an owner
     **/
    removeOwnerEmail(ownerIndex, emailIndex) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            let emails = owners[ownerIndex].email_addresses.filter((_, idx) => idx !== emailIndex);

            owners[ownerIndex] = { ...owners[ownerIndex], email_addresses: emails };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    };

    /**
     ** addOwnerMailingAddress
     **      add a mailing address to an owner
     **/
    addOwnerMailingAddress(ownerIndex) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const mailing_addresses = [...owners[ownerIndex].mailing_addresses, {"address_type": 'Primary', "address_line1": '', "address_line2": '', "city": '', "state_province_region": 'TX', "postal_code": '', "country": 'USA', show: true}];
            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };
            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerMailingAddressLine1Change
     **      change address line 1 for mailing address
     **/
    handleOwnerMailingAddressLine1Change(ownerIndex, mailIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const mailing_addresses = [...owners[ownerIndex].mailing_addresses];

            mailing_addresses[mailIndex].address_line1 = value;
            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerMailingAddressLine2Change
     **      change address line 2 for mailing address
     **/
    handleOwnerMailingAddressLine2Change(ownerIndex, mailIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const mailing_addresses = [...owners[ownerIndex].mailing_addresses];

            mailing_addresses[mailIndex].address_line2 = value;
            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerMailingAddressCityChange
     **      change city for mailing address
     **/
    handleOwnerMailingAddressCityChange(ownerIndex, mailIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const mailing_addresses = [...owners[ownerIndex].mailing_addresses];

            mailing_addresses[mailIndex].city = value;
            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerMailingAddressStateProvinceRegionChange
     **      change state/province/region for mailing address
     **/
    handleOwnerMailingAddressStateProvinceRegionChange(ownerIndex, mailIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const mailing_addresses = [...owners[ownerIndex].mailing_addresses];

            mailing_addresses[mailIndex].state_province_region = value;
            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerMailingAddressPostalCodeChange
     **      change postal code for mailing address
     **/
    handleOwnerMailingAddressPostalCodeChange(ownerIndex, mailIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const mailing_addresses = [...owners[ownerIndex].mailing_addresses];

            mailing_addresses[mailIndex].postal_code = value;
            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerMailingAddressCountryChange
     **      change country for mailing address
     **/
    handleOwnerMailingAddressCountryChange(ownerIndex, mailIndex, value) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const mailing_addresses = [...owners[ownerIndex].mailing_addresses];

            mailing_addresses[mailIndex].country = value;
            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** removeOwnerMailingAddress
     **      remove a mailing address from an owner
     **/
    removeOwnerMailingAddress(ownerIndex, mailIndex) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            let mailing_addresses = owners[ownerIndex].mailing_addresses.filter((_, idx) => idx !== mailIndex);

            owners[ownerIndex] = { ...owners[ownerIndex], mailing_addresses: mailing_addresses };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     * updateArrayItem
     *     utility routine to update an item in an array within the lot state
     */
    updateArrayItem(ownerIndex, arrayName, index, changes) {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            const array = [...(owners[ownerIndex][arrayName] || [])];
            array[index] = { ...array[index], ...changes };
            owners[ownerIndex] = { ...owners[ownerIndex], [arrayName]: array };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    }

    /**
     ** handleOwnerVisibilityChange
     **    handle show/hide change for owner
     **/
    handleOwnerVisibilityChange = (ownerIndex, checked) => {
        this.setState((prev) => {
            const owners = [...prev.lot.owners];
            owners[ownerIndex] = { ...owners[ownerIndex], show_public_directory: checked };

            return { ...prev, lot: { ...prev.lot, owners } };
        });
    };

    /**
     ** handlePhoneVisibilityChange
     **    handle show/hide change for phone number
     **/
    handlePhoneVisibilityChange = (ownerIndex, index, checked) => {
        this.updateArrayItem(ownerIndex, "phone_numbers", index, { show_public_directory: checked });
    };

    /**
     ** handleEmailVisibilityChange
     **    handle show/hide change for email address
     **/
    handleEmailVisibilityChange = (ownerIndex, index, checked) => {
        this.updateArrayItem(ownerIndex, "email_addresses", index, { show_public_directory: checked });
    };

    /**
     ** handleMailingVisibilityChange
     **    handle show/hide change for mailing address
     **/
    handleMailingVisibilityChange = (ownerIndex, index, checked) => {
        this.updateArrayItem(ownerIndex, "mailing_addresses", index, { show_public_directory: checked });
    };

    /**
     ** handleSubmit
     **      handle form submission to save edited lot
     **/
    async handleSubmit(e) {
        const setSaving = this.setSaving.bind(this);
        const lot = this.state.lot;
        const onSave = this.props.onSave;
        const onCancel = this.props.onCancel;

        e.preventDefault();

        if (!onSave) {
            return;
        }

        setSaving(true);

        try {
            await onSave(lot);
        } finally {
            /** 
             ** note: use this technique to handle possible issues with state update when save 
             **   if onSave cleared editLot state which renders DirectoryEntry again, then
             **   attempt to setSaving flag to false generates warning about setting state on unmounted component
             **/
            setSaving(false);
            onCancel();
        }
    }

    /**
     * componentDidMount
     *      react js calls routine once UI components are loaded
     */
    componentDidMount() {
        // Set initial nav items
        if (this.state.isMounted === false && this.props.setNavItems && typeof this.props.setNavItems === "function") {
            const onCancel = this.props.onCancel;
            const handleSubmit = this.handleSubmit.bind(this);
            const navItems = [
                { link: "#", label: "Save", icon: null, onclick: handleSubmit },
                { link: "#", label: "Cancel", icon: null, onclick: onCancel },
            ];
            this.props.setNavItems(navItems);
            this.setState({ isMounted: true });
        }
    }
    
    /**
     * componentWillUnmount
     *      react js calls routine once UI components are unloaded
     */
    componentWillUnmount() {
        // Clear nav items
        if (this.props.setNavItems && typeof this.props.setNavItems === "function") {
            this.props.setNavItems([]);
        }
    }

    /**
     ** render
     **      render the edit lot form
     **/
    render() {
        const handleSubmit = this.handleSubmit.bind(this);
        const handleOwnerChange = this.handleOwnerChange.bind(this);
        const addOwner = this.addOwner.bind(this);
        const removeOwner = this.removeOwner.bind(this);
        const moveOwnerUp = this.moveOwnerUp.bind(this);
        const moveOwnerDown = this.moveOwnerDown.bind(this);
        const updateArrayItem = this.updateArrayItem.bind(this);
        const addOwnerPhone = this.addOwnerPhone.bind(this);
        const removeOwnerPhone = this.removeOwnerPhone.bind(this);
        const handleOwnerVisibilityChange = this.handleOwnerVisibilityChange.bind(this);
        const handlePhoneVisibilityChange = this.handlePhoneVisibilityChange.bind(this);
        const handleEmailVisibilityChange = this.handleEmailVisibilityChange.bind(this);
        const handleMailingVisibilityChange = this.handleMailingVisibilityChange.bind(this);
        const addOwnerEmail = this.addOwnerEmail.bind(this);
        const removeOwnerEmail = this.removeOwnerEmail.bind(this);
        const addOwnerMailingAddress = this.addOwnerMailingAddress.bind(this);
        const handleOwnerMailingAddressLine1Change = this.handleOwnerMailingAddressLine1Change.bind(this);
        const handleOwnerMailingAddressLine2Change = this.handleOwnerMailingAddressLine2Change.bind(this);
        const handleOwnerMailingAddressCityChange = this.handleOwnerMailingAddressCityChange.bind(this);
        const handleOwnerMailingAddressStateProvinceRegionChange = this.handleOwnerMailingAddressStateProvinceRegionChange.bind(this);
        const handleOwnerMailingAddressPostalCodeChange = this.handleOwnerMailingAddressPostalCodeChange.bind(this);
        const handleOwnerMailingAddressCountryChange = this.handleOwnerMailingAddressCountryChange.bind(this);
        const removeOwnerMailingAddress = this.removeOwnerMailingAddress.bind(this);
        const onCancel = this.props.onCancel;
        const lot = this.state.lot;
        const address_line1 = (lot && lot.lot_address) ? lot.lot_address.address_line1 : '';
        const lot_number = (lot) ? lot.lot_number : '';
        const saving = this.state.saving;

        return (
            <div>
            <header className="edit-lot-header">
            <h1>Edit {address_line1 + " (Lot #" + lot_number + ")"}</h1>
            </header>
            <div className="edit-lot-root">
            <form className="edit-lot-form" onSubmit={handleSubmit}>
                {/* Owners */}
                <section className="edit-lot-section">
                    <div className="edit-lot-section-header">
                        <h2>Owners</h2>
                        <button
                        type="button"
                        className="btn-secondary"
                        onClick={addOwner}
                        >
                        + Add Owner
                        </button>
                    </div>

                    {lot.owners.map((owner, ownerIndex) => (
                        <div key={owner.id || ownerIndex} className="owner-card">
                        <div className="owner-card-header">
                            <h3>Owner {ownerIndex + 1}</h3>
                            <div className="owner-card-controls">
                                <button
                                type="button"
                                className="btn-icon btn-reorder"
                                onClick={() => moveOwnerUp(ownerIndex)}
                                disabled={ownerIndex === 0}
                                title="Move owner up"
                                >
                                ▲
                                </button>
                                <button
                                type="button"
                                className="btn-icon btn-reorder"
                                onClick={() => moveOwnerDown(ownerIndex)}
                                disabled={ownerIndex === lot.owners.length - 1}
                                title="Move owner down"
                                >
                                ▼
                                </button>
                                <button
                                type="button"
                                className="btn-icon"
                                onClick={() => removeOwner(ownerIndex)}
                                disabled={lot.owners.length === 1}
                                title="Remove owner"
                                >
                                ✕
                                </button>
                            </div>
                        </div>

                        <div className="edit-lot-subsection">
                                <div key={ownerIndex} className="owner-row">
                                    <input
                                        className="owner-value-input"
                                        type="text"
                                        value={owner.owner_name}
                                        placeholder="Owner Name"
                                        onChange={(e) =>
                                            handleOwnerChange(ownerIndex, "owner_name", e.target.value)
                                        }
                                        required
                                    />
                                    <div className="owner-publish-toggle">
                                        <input
                                            type="checkbox"
                                            id={`showOwner-${ownerIndex}`}
                                            checked={
                                            // default to true when undefined for old data
                                            (owner.show_public_directory !== false && owner.show_public_directory !== 0 && owner.show_public_directory !== undefined && owner.show_public_directory !== null) ? true : false
                                            }
                                            onChange={(e) =>
                                            handleOwnerVisibilityChange(ownerIndex, e.target.checked)
                                            }
                                        />
                                        <label htmlFor={`showOwner-${ownerIndex}`}>publish</label>
                                    </div>
                                </div>
                        </div>
                        {/* Phones */}
                        <div className="edit-lot-subsection">
                            <div className="edit-lot-section-header">
                                <h3 className="edit-lot-section-title">Phone Numbers</h3>
                            </div>

                            <div className="phone-list">
                                {(owner.phone_numbers) ? owner.phone_numbers.map((phone, index) => (
                                <div key={index} className="phone-row">
                                    <input
                                    className="phone-value-input"
                                    type="tel"
                                    value={phone.phone_number || ""}
                                    placeholder="Phone number"
                                    onChange={(e) =>
                                        updateArrayItem(ownerIndex, "phone_numbers", index, { phone_number: e.target.value })
                                    }
                                    />

                                    <div className="phone-publish-toggle">
                                    <input
                                        type="checkbox"
                                        id={`showPhone-${ownerIndex}-${index}`}
                                        checked={
                                        // default to true when undefined for old data
                                        (phone.show_public_directory !== false && phone.show_public_directory !== 0 && phone.show_public_directory !== undefined && phone.show_public_directory !== null) ? true : false
                                        }
                                        onChange={(e) =>
                                        handlePhoneVisibilityChange(ownerIndex, index, e.target.checked)
                                        }
                                    />
                                    <label htmlFor={`showPhone-${ownerIndex}-${index}`}>publish</label>
                                    </div>

                                    <button
                                        type="button"
                                        className="phone-remove-button"
                                        onClick={() =>
                                            removeOwnerPhone(ownerIndex, index)
                                        }
                                        disabled={owner.phone_numbers.length === 0}
                                        title="Remove phone"
                                        >
                                        X
                                    </button>
                                </div>
                                )) : null}
                            </div>
                            <div className="phone-list-footer">
                                <button
                                type="button"
                                className="small-add-button"
                                onClick={() =>
                                    addOwnerPhone(ownerIndex)
                                    }
                                >
                                + Add phone
                                </button>
                            </div>
                        </div>
                        {/* Emails */}
                        <div className="edit-lot-subsection">
                            <div className="edit-lot-section-header">
                                <h3 className="edit-lot-section-title">Emails</h3>
                            </div>

                            <div className="email-list">
                                {(owner.email_addresses) ? owner.email_addresses.map((email, index) => (
                                <div key={index} className="email-row">
                                    <input
                                    className="email-value-input"
                                    type="email"
                                    value={email.email_address || ""}
                                    placeholder="Email address"
                                    onChange={(e) =>
                                        updateArrayItem(ownerIndex, "email_addresses", index, { email_address: e.target.value })
                                    }
                                    />

                                    <div className="email-publish-toggle">
                                    <input
                                        type="checkbox"
                                        id={`showEmail-${ownerIndex}-${index}`}
                                        checked={
                                        // default to true when undefined for old data
                                        (email.show_public_directory !== false && email.show_public_directory !== 0 && email.show_public_directory !== undefined && email.show_public_directory !== null) ? true : false
                                        }
                                        onChange={(e) =>
                                        handleEmailVisibilityChange(ownerIndex, index, e.target.checked)
                                        }
                                    />
                                    <label htmlFor={`showEmail-${ownerIndex}-${index}`}>publish</label>
                                    </div>

                                    <button
                                        type="button"
                                        className="email-remove-button"
                                        onClick={() =>
                                            removeOwnerEmail(ownerIndex, index)
                                        }
                                        disabled={owner.email_addresses.length === 0}
                                        title="Remove email"
                                        >
                                        X
                                    </button>
                                </div>
                                )) : null}
                            </div>
                            <div className="email-list-footer">
                                <button
                                type="button"
                                className="small-add-button"
                                onClick={() =>
                                    addOwnerEmail(ownerIndex)
                                    }
                                >
                                + Add email
                                </button>
                            </div>
                        </div>
                        {/* Mailing Addresses */}
                        <div className="edit-lot-subsection">
                            <div className="edit-lot-section-header">
                                <h3 className="edit-lot-section-title">Addresses</h3>
                            </div>

                            <div className="mailing-list">
                            {owner.mailing_addresses.map((mailing_address, mailIndex) => (
                                <div key={mailIndex} className="mailing-address-block">
                                    <div key={mailIndex+"addressline1"} className="edit-dir-form-row align-center">
                                        <input
                                            className="email-value-input"
                                            type="address_line1"
                                            value={mailing_address.address_line1}
                                            onChange={(e) =>
                                            handleOwnerMailingAddressLine1Change(
                                                ownerIndex,
                                                mailIndex,
                                                e.target.value
                                            )
                                            }
                                            placeholder="123 Main St"
                                        />
                                    </div>
                                    <div key={mailIndex+"addressline2"} className="edit-dir-form-row align-center">
                                        <input
                                            className="email-value-input"
                                            type="address_line2"
                                            value={mailing_address.address_line2}
                                            onChange={(e) =>
                                            handleOwnerMailingAddressLine2Change(
                                                ownerIndex,
                                                mailIndex,
                                                e.target.value
                                            )
                                            }
                                            placeholder="Apt 4B"
                                        />
                                    </div>
                                    <div key={mailIndex+"city-state-zip-row"} className="city-state-row">
                                            <input
                                                className="email-value-input"
                                                type="city"
                                                value={mailing_address.city}
                                                onChange={(e) =>
                                                handleOwnerMailingAddressCityChange(
                                                    ownerIndex,
                                                    mailIndex,
                                                    e.target.value
                                                )
                                                }
                                                placeholder="City"
                                            />
                                            <input
                                                className="email-value-input"
                                                type="state_province_region"
                                                value={mailing_address.state_province_region}
                                                onChange={(e) =>
                                                handleOwnerMailingAddressStateProvinceRegionChange(
                                                    ownerIndex,
                                                    mailIndex,
                                                    e.target.value
                                                )
                                                }
                                                placeholder="State/Province/Region"
                                            />
                                    </div>
                                    <div key={mailIndex+"country_remove"} className="zip-country-publish-remove-row">
                                        <input
                                            className="email-value-input"
                                            value={mailing_address.postal_code}
                                            onChange={(e) =>
                                            handleOwnerMailingAddressPostalCodeChange(
                                                ownerIndex,
                                                mailIndex,
                                                e.target.value
                                            )
                                            }
                                            placeholder="Zip/Postal Code"
                                        />
                                        <input
                                            className="email-value-input"
                                            type="country"
                                            value={mailing_address.country}
                                            onChange={(e) =>
                                            handleOwnerMailingAddressCountryChange(
                                                ownerIndex,
                                                mailIndex,
                                                e.target.value
                                            )
                                            }
                                            placeholder="Country"
                                        />
                                        <div className="mailing-publish-toggle">
                                            <input
                                                type="checkbox"
                                                id={`showMailing-${ownerIndex}-${mailIndex}`}
                                                checked={
                                                // default to true when undefined for old data
                                                mailing_address.show_public_directory !== false && mailing_address.show_public_directory !== 0 && mailing_address.show_public_directory !== undefined && mailing_address.show_public_directory !== null ? true : false
                                                }
                                                onChange={(e) =>
                                                handleMailingVisibilityChange(ownerIndex, mailIndex, e.target.checked)
                                                }
                                            />
                                            <label htmlFor={`showMailing-${ownerIndex}-${mailIndex}`}>publish</label>
                                        </div>
                                        <button
                                            type="button"
                                            className="mailing-remove-button"
                                            onClick={() =>
                                                removeOwnerMailingAddress(ownerIndex, mailIndex)
                                            }
                                            disabled={owner.mailing_addresses.length === 0}
                                            title="Remove mailing address"
                                            >
                                            X
                                        </button>
                                    </div> {/** END mailing-country-remove-row **/}
                                </div>))}
                            </div>
                            <div className="mailing-list-footer">
                                <button
                                type="button"
                                className="small-add-button"
                                onClick={() =>
                                    addOwnerMailingAddress(ownerIndex)
                                    }
                                >
                                + Add mailing address
                                </button>
                            </div>
                        </div> {/* End Mailing Addresses */}
                    </div>))} {/** owner card **/}
                </section>
                {/* Actions */}
                <footer className="edit-lot-footer">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={onCancel}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={saving}>
                        {saving ? "Saving…" : "Save Changes"}
                    </button>
                </footer>
            </form>
            </div>
            </div>
        );        
    }
}

export default SLTEditDirectoryEntry;